/* global google */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { debounce } from "@mui/material/utils";

let googleMapsPromise;

function loadGoogleMaps(key) {
  if (window.google?.maps?.places?.AutocompleteSuggestion) {
    return Promise.resolve();
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  const existingScript = Array.from(
    document.getElementsByTagName("script"),
  ).find((s) => s.src.includes("maps.googleapis.com/maps/api/js"));

  if (existingScript) {
    googleMapsPromise = new Promise((resolve, reject) => {
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        resolve();
      } else {
        existingScript.addEventListener("load", resolve);
        existingScript.addEventListener("error", reject);
      }
    });
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places,marker&callback=Function.prototype&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleMapsPromise;
}

function highlightText(text, matches = []) {
  if (!matches.length) return [{ text, highlight: false }];

  let last = 0;
  const parts = [];

  matches.forEach(({ offset, length }) => {
    if (offset > last) {
      parts.push({ text: text.slice(last, offset), highlight: false });
    }
    parts.push({
      text: text.slice(offset, offset + length),
      highlight: true,
    });
    last = offset + length;
  });

  if (last < text.length) {
    parts.push({ text: text.slice(last), highlight: false });
  }

  return parts;
}

function GooglePlaceAutocomplete({
  name,
  control,
  rules,
  errors,
  label,
  placeholder,
  size,
  required,
  disabled,
  helperText,
  startIcon: StartIcon,
  onPlaceSelected,
  apiKey,
  defaultValue,
  countryCodes,
  value: externalValue,
  manualMode = false,
  onChange: externalOnChange,
  startIconClassName,
  startIconColor,
}) {
  const [loaded, setLoaded] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const sessionTokenRef = useRef(null);

  useEffect(() => {
    setInputValue(defaultValue ?? "");
  }, [defaultValue]);

  useEffect(() => {
    const key = apiKey || process.env.REACT_APP_GOOGLE_KEY;
    loadGoogleMaps(key)
      .then(() => setLoaded(true))
      .catch(console.error);
  }, [apiKey]);

  useEffect(() => {
    if (manualMode) {
      setOptions([]);
      sessionTokenRef.current = null;
    }
  }, [manualMode]);

  const fetchSuggestions = useMemo(
    () =>
      debounce(async (input, callback) => {
        if (manualMode) {
          callback([]);
          return;
        }
        if (!input || input.length < 2 || !loaded) {
          callback([]);
          return;
        }

        if (!sessionTokenRef.current) {
          sessionTokenRef.current =
            new google.maps.places.AutocompleteSessionToken();
        }
        function getAutocompleteOptions(countryCodesInput) {
          const codes = []
            .concat(countryCodesInput || [])
            .map((c) => (c || "").toLowerCase());

          let includedRegionCodes;
          let locationBias;
          let locationRestriction;

          if (codes.includes("au") || codes.includes("nz")) {
            includedRegionCodes = ["AU", "NZ"];
          } else if (codes.includes("us")) {
            includedRegionCodes = ["US"];
          } else if (codes.includes("eu")) {
            locationRestriction = {
              north: 71,
              south: 34.5,
              west: -25,
              east: 45,
            };
            locationBias = undefined;
            includedRegionCodes = undefined;
          }

          return {
            input,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes,
            locationBias,
            locationRestriction,
          };
        }

        try {
          const options = getAutocompleteOptions(countryCodes);

          const { suggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
              options,
            );

          const formatted = suggestions.map((s) => {
            const p = s.placePrediction;
            return {
              description: p.text.text,
              placeId: p.placeId,
              structured_formatting: {
                main_text: p.mainText.text,
                main_text_matched_substrings: p.mainText.matches.map((m) => ({
                  offset: m.startOffset,
                  length: m.endOffset - m.startOffset,
                })),
                secondary_text: p.secondaryText?.text || "",
              },
            };
          });

          callback(formatted);
        } catch (err) {
          err;
          // console.error("Autocomplete error:", err);
          callback([]);
        }
      }, 400),
    [loaded, countryCodes],
  );

  const fetchPlaceDetails = async (placeId) => {
    if (manualMode) return null;
    const place = new google.maps.places.Place({ id: placeId });

    await place.fetchFields({
      fields: [
        "formattedAddress",
        "addressComponents",
        "location",
        "displayName",
        "types",
      ],
    });

    return {
      place_id: place.id,
      formatted_address: place.formattedAddress,
      address_components: place.addressComponents,
      geometry: {
        location: {
          lat: () => place.location.lat(),
          lng: () => place.location.lng(),
        },
      },
      name: place.displayName?.text || null,
      types: place.types || [],
    };
  };

  const errorMessage = errors?.[name]?.message || helperText;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue || ""}
      render={({ field }) => {
        const value = externalValue ?? field?.value ?? "";
        const onChange = externalOnChange ?? field?.onChange ?? (() => {});

        return (
          <Autocomplete
            freeSolo
            options={manualMode ? [] : options}
            value={value}
            inputValue={inputValue}
            disabled={disabled}
            filterOptions={(x) => x}
            isOptionEqualToValue={() => false}
            onInputChange={(e, newValue, reason) => {
              const safeValue = newValue ?? "";
              setInputValue(safeValue);

              if (reason === "input") {
                fetchSuggestions(safeValue, setOptions);
              }

              if (reason === "input" || reason === "clear") {
                onChange(safeValue);
              }
            }}
            onChange={async (e, selected, reason) => {
              if (manualMode) return;
              if (reason === "clear") {
                setInputValue("");
                setOptions([]);
                onChange("");
                return;
              }

              if (!selected) return;

              if (typeof selected === "string") {
                setInputValue(selected);
                onChange(selected);
                return;
              }

              try {
                const details = await fetchPlaceDetails(selected.placeId);
                setInputValue(details.formatted_address);
                onChange(details.formatted_address);

                onPlaceSelected?.(details);
                sessionTokenRef.current = null;
                setOptions([]);
              } catch (err) {
                console.error(err);
              }
            }}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.description
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                size={size}
                required={required}
                error={!!errors?.[name]}
                helperText={errorMessage}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: StartIcon ? (
                    <InputAdornment
                      position="start"
                      sx={{ paddingLeft: "9px", marginRight: "0px !important" }}
                    >
                      <StartIcon
                        className={startIconClassName}
                        style={{ color: startIconColor }}
                      />
                    </InputAdornment>
                  ) : null,
                  endAdornment: null,
                  sx: {
                    "& input": {
                      paddingLeft: "0 !important",
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => {
              const parts = highlightText(
                option.structured_formatting.main_text,
                option.structured_formatting.main_text_matched_substrings,
              );
              const { key, ...rest } = props;
              return (
                <li key={key} {...rest}>
                  <Grid container alignItems="center">
                    <Grid sx={{ width: 36 }}>
                      <LocationOnIcon />
                    </Grid>
                    <Grid sx={{ width: "calc(100% - 36px)" }}>
                      {parts.map((p, i) => (
                        <Box
                          key={i}
                          component="span"
                          sx={{ fontWeight: p.highlight ? 700 : 400 }}
                        >
                          {p.text}
                        </Box>
                      ))}
                      <Typography variant="body2" color="text.secondary">
                        {option.structured_formatting.secondary_text}
                      </Typography>
                    </Grid>
                  </Grid>
                </li>
              );
            }}
          />
        );
      }}
    />
  );
}

GooglePlaceAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  errors: PropTypes.object,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium"]),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  startIcon: PropTypes.elementType,
  onPlaceSelected: PropTypes.func,
  apiKey: PropTypes.string,
  defaultValue: PropTypes.string,
  countryCodes: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  value: PropTypes.string,
  onChange: PropTypes.func,
  manualMode: PropTypes.bool,
  startIconClassName: PropTypes.string,
  startIconColor: PropTypes.string,
  key: PropTypes.integer,
};

GooglePlaceAutocomplete.defaultProps = {
  rules: {},
  errors: null,
  label: "Address",
  placeholder: "",
  size: "small",
  required: false,
  disabled: false,
  helperText: "",
  startIcon: HomeIcon,
  onPlaceSelected: null,
  apiKey: null,
  defaultValue: "",
  countryCodes: null,
};

export default GooglePlaceAutocomplete;
